/**
 * Zuora Reconciliation Queue — admin monitoring page.
 *
 * Surfaces the health of the object-aware Zuora pipeline so operators can watch
 * the format switch (cutover step "B") when it goes live: per-status/reason
 * queue counts, stale (>24h) backlog, blocked rows, and object-stream ingest
 * failures (a rising failed count = a new Zuora object type to add to the
 * normalizer). Until the switch is live the queue is dormant (empty) — that is
 * the expected healthy state and the page says so.
 *
 * Route: /admin/zuora-reconciliation (super_admin / admin only).
 */

import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { format, formatDistanceToNow } from "date-fns";
import Button from "../../components/common/Button";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import ZuoraWorkerHealthPanel from "../../components/admin/ZuoraWorkerHealthPanel";
import {
  getReconQueueSummary,
  getBlockedAndPendingRows,
  getObjectIngestStats,
  type ReconQueueSummaryRow,
} from "../../services/zuoraReconciliation.service";
import ZuoraIdentityReviews from "../../components/admin/ZuoraIdentityReviews";

const REFRESH_MS = 30_000;

function statusBadgeClass(status: string): string {
  switch (status) {
    case "processed":
      return "bg-green-100 text-green-800";
    case "pending":
      return "bg-yellow-100 text-yellow-800";
    case "blocked":
      return "bg-red-100 text-red-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
}

function StatCard({
  label,
  value,
  tone = "default",
  hint,
}: {
  label: string;
  value: number | string;
  tone?: "default" | "warn" | "danger" | "ok";
  hint?: string;
}) {
  const toneClass =
    tone === "danger"
      ? "text-red-600"
      : tone === "warn"
        ? "text-yellow-600"
        : tone === "ok"
          ? "text-green-600"
          : "text-gray-900";
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
        {label}
      </div>
      <div className={`mt-1 text-2xl font-bold ${toneClass}`}>{value}</div>
      {hint && <div className="mt-1 text-xs text-gray-400">{hint}</div>}
    </div>
  );
}

function fmtTs(ts: string | null): string {
  if (!ts) {
    return "—";
  }
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) {
    return "—";
  }
  return format(d, "MMM d, yyyy h:mm a");
}

function fmtAge(ts: string | null): string {
  if (!ts) {
    return "—";
  }
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) {
    return "—";
  }
  return `${formatDistanceToNow(d)} ago`;
}

const ZuoraReconciliationQueue: React.FC = () => {
  const summaryQ = useQuery({
    queryKey: ["zuora-recon", "summary"],
    queryFn: getReconQueueSummary,
    refetchInterval: REFRESH_MS,
  });
  const rowsQ = useQuery({
    queryKey: ["zuora-recon", "rows"],
    queryFn: getBlockedAndPendingRows,
    refetchInterval: REFRESH_MS,
  });
  const ingestQ = useQuery({
    queryKey: ["zuora-recon", "ingest"],
    queryFn: getObjectIngestStats,
    refetchInterval: REFRESH_MS,
  });

  const summary: ReconQueueSummaryRow[] = summaryQ.data ?? [];
  const totalsByStatus = summary.reduce<Record<string, number>>((acc, r) => {
    acc[r.status] = (acc[r.status] ?? 0) + Number(r.n);
    return acc;
  }, {});
  const totalStale = summary.reduce(
    (sum, r) => sum + Number(r.older_than_24h),
    0,
  );

  const refreshing =
    summaryQ.isFetching || rowsQ.isFetching || ingestQ.isFetching;
  const anyError = summaryQ.error || rowsQ.error || ingestQ.error;

  const refetchAll = () => {
    summaryQ.refetch();
    rowsQ.refetch();
    ingestQ.refetch();
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Zuora Reconciliation Queue
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Health of the object-aware Zuora pipeline. Auto-refreshes every 30s.
          </p>
          <Link
            to="/admin/zuora-held-welcomes"
            className="mt-1 inline-block text-sm text-blue-600 hover:underline"
          >
            Held welcomes (review and release)
          </Link>
        </div>
        <Button onClick={refetchAll} variant="secondary" disabled={refreshing}>
          {refreshing ? "Refreshing…" : "Refresh"}
        </Button>
      </div>

      {anyError && (
        <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          Failed to load monitoring data:{" "}
          {(anyError as Error).message ?? "unknown error"}
        </div>
      )}

      {/*
        Worker health first: it answers "should we ramp at all", which gates
        every decision the queue numbers below inform. Owns its own query and
        window selector, so it refreshes independently of this page's Refresh
        button and a worker-health failure cannot blank the queue tables.
      */}
      <ZuoraWorkerHealthPanel />

      {/* Headline stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
        <StatCard
          label="Pending"
          value={totalsByStatus["pending"] ?? 0}
          tone={totalsByStatus["pending"] ? "warn" : "default"}
          hint="awaiting reconcile"
        />
        <StatCard
          label="Blocked"
          value={totalsByStatus["blocked"] ?? 0}
          tone={totalsByStatus["blocked"] ? "danger" : "default"}
          hint="stale / errored"
        />
        <StatCard
          label="Stale >24h"
          value={totalStale}
          tone={totalStale ? "danger" : "default"}
          hint="oldest backlog"
        />
        <StatCard
          label="Object ingest failed"
          value={ingestQ.data?.failed ?? 0}
          tone={ingestQ.data?.failed ? "danger" : "default"}
          hint="unknown type → normalizer"
        />
        <StatCard
          label="Object ingest pending"
          value={ingestQ.data?.pending ?? 0}
          tone={ingestQ.data?.pending ? "warn" : "default"}
          hint="staged, not reconciled"
        />
      </div>

      {/* Summary by status / reason */}
      <section className="mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-3">
          Queue by status &amp; reason
        </h2>
        {summaryQ.isLoading ? (
          <LoadingSpinner />
        ) : summary.length === 0 ? (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-6 text-sm text-gray-600">
            The reconciliation queue is empty. The object-stream pipeline is
            dormant — production is currently 100% legacy basicInfo format. This
            is the expected healthy state until the Zuora format switch goes
            live.
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">
                    Status
                  </th>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">
                    Reason
                  </th>
                  <th className="px-4 py-2 text-right font-medium text-gray-500">
                    Count
                  </th>
                  <th className="px-4 py-2 text-right font-medium text-gray-500">
                    &gt; 24h
                  </th>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">
                    Oldest
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {summary.map((r, i) => (
                  <tr key={`${r.status}-${r.reason ?? "none"}-${i}`}>
                    <td className="px-4 py-2">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusBadgeClass(r.status)}`}
                      >
                        {r.status}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-gray-700">
                      {r.reason ?? "—"}
                    </td>
                    <td className="px-4 py-2 text-right font-medium text-gray-900">
                      {Number(r.n).toLocaleString()}
                    </td>
                    <td
                      className={`px-4 py-2 text-right ${Number(r.older_than_24h) > 0 ? "text-red-600 font-semibold" : "text-gray-400"}`}
                    >
                      {Number(r.older_than_24h).toLocaleString()}
                    </td>
                    <td className="px-4 py-2 text-gray-500">
                      {fmtTs(r.oldest)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Blocked & pending row detail */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">
          Blocked &amp; pending rows
          {rowsQ.data && rowsQ.data.length > 0 && (
            <span className="ml-2 text-sm font-normal text-gray-500">
              (showing {rowsQ.data.length}, oldest first)
            </span>
          )}
        </h2>
        {rowsQ.isLoading ? (
          <LoadingSpinner />
        ) : !rowsQ.data || rowsQ.data.length === 0 ? (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-6 text-sm text-gray-600">
            No blocked or pending rows.
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">
                    Account / Contact
                  </th>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">
                    Status
                  </th>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">
                    Reason
                  </th>
                  <th className="px-4 py-2 text-right font-medium text-gray-500">
                    Attempts
                  </th>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">
                    Age
                  </th>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">
                    Last error
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {rowsQ.data.map((r) => (
                  <tr key={r.id}>
                    <td className="px-4 py-2 font-mono text-xs text-gray-700">
                      {r.zuora_account_id ?? r.zuora_contact_id ?? "—"}
                    </td>
                    <td className="px-4 py-2">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusBadgeClass(r.status)}`}
                      >
                        {r.status}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-gray-700">
                      {r.last_wait_reason ?? r.reason ?? "—"}
                    </td>
                    <td className="px-4 py-2 text-right text-gray-700">
                      {r.attempt_count}
                    </td>
                    <td className="px-4 py-2 text-gray-500 whitespace-nowrap">
                      {fmtAge(r.created_at)}
                    </td>
                    <td className="px-4 py-2 text-gray-500 max-w-xs truncate">
                      {r.last_error ?? "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* The identity-review queue lives here rather than on its own route: these reviews
          ARE the blocked slice of the queue above, and splitting them across two pages
          would hide the connection between "rows held" and "why". */}
      <ZuoraIdentityReviews />
    </div>
  );
};

export default ZuoraReconciliationQueue;
